// =============================================================================
// Message Repository - CRUD pour les messages chat
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage } from '../types';

export async function fetchMessages(
  channelId: string,
  opts?: { limit?: number; before?: string; parentOnly?: boolean; afterDate?: string }
): Promise<ChatMessage[]> {
  let query = supabase
    .from('chat_messages')
    .select(`
      *,
      sender:sender_member_id(
        id,
        user_id,
        core_profiles:user_id(full_name, avatar_url, email)
      ),
      chat_reactions(id, emoji, org_member_id),
      chat_message_task_links(
        id, task_id, link_type,
        task:task_id(title, status_code, project_id)
      )
    `)
    .eq('channel_id', channelId);

  if (opts?.parentOnly) {
    query = query.is('parent_message_id', null);
  }

  if (opts?.before) {
    query = query.lt('created_at', opts.before);
  }

  // Filter messages after soft-delete date
  if (opts?.afterDate) {
    query = query.gt('created_at', opts.afterDate);
  }

  // IMPORTANT: fetch the MOST RECENT N messages, then reverse to chronological order.
  // Previously this used ascending order with a limit, which returned the OLDEST N
  // messages and made newer messages disappear after refresh once a channel exceeded
  // the limit (realtime kept showing them live, but reloads silently dropped them).
  // `before` (created_at) is used by the infinite-scroll path to paginate upward.
  query = query
    .order('created_at', { ascending: false })
    .limit(opts?.limit ?? 100);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map(mapMessage).reverse();
}

export async function fetchThreadMessages(parentMessageId: string): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select(`
      *,
      sender:sender_member_id(
        id,
        user_id,
        core_profiles:user_id(full_name, avatar_url, email)
      ),
      chat_reactions(id, emoji, org_member_id)
    `)
    .eq('parent_message_id', parentMessageId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapMessage);
}

export async function fetchMessageById(messageId: string): Promise<ChatMessage | null> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select(`
      *,
      sender:sender_member_id(
        id,
        user_id,
        core_profiles:user_id(full_name, avatar_url, email)
      ),
      chat_reactions(id, emoji, org_member_id),
      chat_message_task_links(
        id, task_id, link_type,
        task:task_id(title, status_code, project_id)
      )
    `)
    .eq('id', messageId)
    .maybeSingle();

  if (error) throw error;
  return data ? mapMessage(data) : null;
}

export async function sendMessage(params: {
  orgId: string;
  channelId: string;
  senderMemberId: string;
  content: string;
  parentMessageId?: string | null;
}): Promise<ChatMessage> {
  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      org_id: params.orgId,
      channel_id: params.channelId,
      sender_member_id: params.senderMemberId,
      content: params.content,
      parent_message_id: params.parentMessageId || null,
    })
    .select(`
      *,
      sender:sender_member_id(
        id,
        user_id,
        core_profiles:user_id(full_name, avatar_url, email)
      )
    `)
    .single();

  if (error) throw error;
  return mapMessage(data);
}

export async function editMessage(messageId: string, content: string): Promise<void> {
  const { error } = await supabase
    .from('chat_messages')
    .update({ content, is_edited: true, edited_at: new Date().toISOString() })
    .eq('id', messageId);

  if (error) throw error;
}

export async function deleteMessage(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_messages')
    .delete()
    .eq('id', messageId);

  if (error) throw error;
}

// Thread count for a message
export async function fetchThreadCount(messageId: string): Promise<number> {
  const { count, error } = await supabase
    .from('chat_messages')
    .select('id', { count: 'exact', head: true })
    .eq('parent_message_id', messageId);

  if (error) throw error;
  return count || 0;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapMessage(raw: any): ChatMessage {
  const rawProfile = raw.sender?.core_profiles;
  const profile = Array.isArray(rawProfile) ? rawProfile[0] : rawProfile;
  return {
    ...raw,
    sender: profile ? {
      full_name: profile.full_name ?? null,
      avatar_url: profile.avatar_url ?? null,
      email: profile.email ?? '',
      member_id: raw.sender?.id || raw.sender_member_id,
    } : undefined,
    reactions: raw.chat_reactions || [],
    task_links: (raw.chat_message_task_links || []).map((link: any) => ({
      ...link,
      task: link.task || undefined,
    })),
  };
}
